import { ORDER_TYPE } from 'common/constants/app.constants';
import { filterAccounts, findParamFromUrl, formatDate } from '../../common/utils/app.utils';
import { ADDRESS_TYPE_ENUM } from '../components/summary/summary.constant';

const updateOrderSchema = {
  billingAddress: {
    addressLine1: '',
    city: '',
    officeName: '',
    pinCode: ''
  },
  billingLevel: '',
  billingType: '',
  billingCycle: '',
  gstNumber: '',
  panNumber: '',
  sezSite: '',
  dty: '',
  crmNbaId: '',
  poId: null,
  shippingAddress: {
    addressLine1: '',
    city: '',
    pinCode: '',
    officeName: ''
  },
  billingContactDetails: {
    email: '',
    phoneNumber: '',
    firstName: '',
    address: {
      m6Id: ''
    }
  },
  customerNeedDate: ''
};

export const getContactsDetails = ({ contacts }) => {
  if (Array.isArray(contacts) && contacts.length > 0) {
    const { contactId, contactType } = contacts[0];
    return { contactId, contactType };
  }
  return {};
};

const setBillingAddress = (updateOrderSchema, billingForm, orderDetail, customerAccounts = []) => {
  if (billingForm.selected.id === ADDRESS_TYPE_ENUM.EXISTINGADDRESS) {
    updateOrderSchema['billingAddress']['addressLine1'] = billingForm.existingAddress.addressLine1 || '';
    updateOrderSchema['billingAddress']['city'] = billingForm.existingAddress.city || '';
    updateOrderSchema['billingAddress']['pinCode'] = billingForm.existingAddress.pinCode || '';
  } else {
    updateOrderSchema['billingAddress']['addressLine1'] = billingForm.address.value || '';
    updateOrderSchema['billingAddress']['city'] = billingForm.city.value.key || '';
    updateOrderSchema['billingAddress']['pinCode'] = billingForm.pinCode.value || '';
    updateOrderSchema['billingContactDetails'] = {
      email: billingForm.email.value || '',
      phoneNumber: billingForm.mobileNumber.value || '',
      firstName: billingForm.personName.value || '',
      address: {
        m6Id: billingForm.billableId.value
      },
      ...getContactsDetails(orderDetail)
    };
  }
  updateOrderSchema['billingAddress']['officeName'] = billingForm.companyName || '';
  updateOrderSchema['billingLevel'] = billingForm.type.id || '';
  updateOrderSchema['billingType'] = (billingForm.paymentType && billingForm.paymentType.value && billingForm.paymentType.value.key) || '';
  updateOrderSchema['billingCycle'] = (billingForm.paymentCycle && billingForm.paymentCycle.value && billingForm.paymentCycle.value.key) || '';
  updateOrderSchema['crmNbaId'] = (billingForm.accountNumber && billingForm.accountNumber.value && billingForm.accountNumber.value.key) || '';

  const selectedAccount = filterAccounts(customerAccounts, findParamFromUrl('partyId', false))?.find(
    (item) => item?.parentAccountNumber === billingForm?.accountNumber?.value?.key
  );
  if (selectedAccount) {
    updateOrderSchema['orderToBeLoggedInSiebel'] = selectedAccount?.orderToBeLoggedInSiebel;
  }

  if (billingForm?.type?.id === 'PO') {
    updateOrderSchema['poId'] = billingForm?.poId?.value?.key ?? null;
  } else {
    updateOrderSchema['poId'] = null;
  }

  return updateOrderSchema;
};

const setShippingAddress = (updateOrderSchema, form) => {
  if (form.selected.id === ADDRESS_TYPE_ENUM.EXISTINGADDRESS) {
    if (form.existingAddress.key === 'billingAddress') {
      updateOrderSchema['shippingAddress'] = updateOrderSchema.billingAddress;
    } else {
      updateOrderSchema['shippingAddress'] = form.installationAddress;
    }
  } else {
    updateOrderSchema['shippingAddress']['addressLine1'] = form.address.value || '';
    updateOrderSchema['shippingAddress']['city'] = form.city.value.key || '';
    updateOrderSchema['shippingAddress']['pinCode'] = form.pinCode.value || '';
    updateOrderSchema['shippingAddress']['officeName'] = form.officeName || '';
  }

  return updateOrderSchema;
};

const setPurchaseDetail = (updateOrderSchema, purchaseDetail) => {
  if (purchaseDetail) {
    updateOrderSchema['purchaseOrderAmount'] = purchaseDetail.purchaseOrderTotalCharges.value || '';
    updateOrderSchema['purchaseOrderDate'] = formatDate(purchaseDetail.purchaseOrderDate.value) || '';
    updateOrderSchema['purchaseOrderNumber'] = purchaseDetail.purchaseOrderNo.value || '';
    updateOrderSchema['purchaseOrderExpiryDate'] = (purchaseDetail.purchaseOrderExpiryDate.value && formatDate(purchaseDetail.purchaseOrderExpiryDate.value)) || '';
  } else {
    updateOrderSchema['purchaseOrderNumber'] = 'NA';
  }
  return updateOrderSchema;
};

export const getRequestForOrderUpdate = (form, orderDetail, isMultiSite, customerAccounts = []) => {
  const panNumber = orderDetail.lead.customer.panNumber || '';
  const encryptedPanNumber = btoa(panNumber);

  updateOrderSchema['panNumber'] = encryptedPanNumber;
  updateOrderSchema['gstNumber'] = isMultiSite ? '' : form && form.addressGSTDetail && form.addressGSTDetail.gstin.value ? form.addressGSTDetail.gstin.value : 'NA';
  updateOrderSchema['sezSite'] = isMultiSite ? '' : form && form.addressGSTDetail && form.addressGSTDetail.sezSite;
  updateOrderSchema['dty'] = isMultiSite ? '' : form && form.addressGSTDetail && form.addressGSTDetail.dty;
  updateOrderSchema['siteIncharge'] = isMultiSite
    ? {}
    : { firstName: form.addressGSTDetail.site.contactName.value, phoneNumber: form.addressGSTDetail.site.mobileNumber.value, email: form.addressGSTDetail.site.email.value };
  updateOrderSchema['customerNeedDate'] = formatDate(form.addressGSTDetail?.delieveryDate?.value) || '';

  setPurchaseDetail(updateOrderSchema, form.purchaseDetail);
  setBillingAddress(updateOrderSchema, form.billingDetails, orderDetail, customerAccounts);
  setShippingAddress(updateOrderSchema, form.shippingDetails);
  return { ...updateOrderSchema };
};

export const prepareOptionsForDropDown = (list, keyName, sortFn, suffix) => {
  const options = [];
  if (Array.isArray(list)) {
    const elements = list.map((element) => (keyName ? element[keyName] : element));
    if (typeof sortFn === 'function') {
      elements.sort(sortFn);
    }
    const values = new Set(elements);
    for (let [key, value] of values.entries()) {
      if (value !== null && value !== undefined && value !== '') {
        options.push({ key, displayName: suffix ? `${value} ${suffix}` : value, value });
      }
    }
  }
  return options;
};

export const prepareOptionForAddress = (contactList, companyName) => {
  return contactList.map(({ address }) => {
    const option = {
      ...address,
      key: address.addressLine1,
      displayName: address.addressLine1,
      city: address.city,
      pinCode: address.pincode,
      officeName: address.officeName ? address.officeName : companyName
    };
    return option;
  });
};

export const isOrderConfirmationUrl = () => findParamFromUrl('type', false) === ORDER_TYPE.ORDER_CONFIRMATION;
